import React, { CSSProperties, ReactNode } from 'react';
import { inject, observer } from 'mobx-react';
import TasksBoard from '../Widget/TasksBoard.tsx';
import {
  Box,
  Button,
  ButtonProps,
  Grid,
  Overlay,
  Space,
  Stack,
} from '@mantine/core';
import {
  IconChartArcs,
  IconClipboardPlus,
  IconFileCheck,
  IconFlag,
  IconHeartbeat,
  IconNotebook,
} from '@tabler/icons-react';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { routeStore } from '../../../global/Store/Store.ts';
import { useFeatureVisibility } from '../../../global/provider/featureVisibilityContext';
import { useFilteredJournals } from '../../../global/entities/Journal/queries/useJournals';
import { PatientStore } from '../../../global/Store/PatientStore';
import { useEICFConsentLock } from '../../../global/component/organism/EICFConsentGuard/eicfConsentLockContext.ts';
import { useGetMySignedEICFConsent } from '../../../global/entities/EICFConsents/queries/useGetMySignedEICFConsent.tsx';

export type IPatientPageProps = {
  patientStore?: PatientStore;
};

const PatientOverview: React.FC<IPatientPageProps> = inject('patientStore')(
  observer(({ patientStore }) => {
    const navigate = useNavigate();
    const { isFeatureVisible } = useFeatureVisibility();
    const { isLocked: isEicfLocked } = useEICFConsentLock();

    const onClickButton = (slideId: string) => {
      routeStore?.setFromLocation();
      navigate(`${slideId}`);
    };

    const showJournal = isFeatureVisible('journal');
    const showMedicalOverview = isFeatureVisible('medicalOverview');
    const showMedicalInformation = isFeatureVisible('medicalInformation');
    const showPhysiologicals = isFeatureVisible('physiologicals');

    // The patient can view their consent once they have signed it. The backend
    // only returns a consent in that state, so its presence is the visibility gate.
    const { data: myConsent } = useGetMySignedEICFConsent();
    const showMyConsent = !!myConsent;

    const { data: journals = [] } = useFilteredJournals(
      patientStore?.therapeuticIndicationId
        ? parseInt(patientStore.therapeuticIndicationId)
        : undefined,
      patientStore?.sexId ? parseInt(patientStore.sexId) : undefined,
    );

    const onAddJournalEntry = () => {
      routeStore?.setFromLocation();
      if (journals.length === 1) {
        const dateString = dayjs().format('YYYY-MM-DD');
        navigate(
          `/patientDashboard/journals/${journals[0].id}/new-entry/${dateString}`,
        );
      } else navigate('/patientDashboard/journals');
    };

    const topButtonCount =
      (showMedicalOverview ? 1 : 0) +
      (showMedicalInformation ? 1 : 0) +
      (showMyConsent ? 1 : 0);
    const bottomButtonCount =
      (showMedicalInformation ? 1 : 0) + (showPhysiologicals ? 1 : 0);
    const topSpan = topButtonCount <= 1 ? 12 : topButtonCount === 2 ? 6 : 4;
    const bottomSpan = bottomButtonCount === 1 ? 12 : 6;

    return (
      <Stack gap={'lg'}>
        <Space h={'sm'} />
        <TasksBoard />
        <Box pos="relative">
          <Stack gap={'lg'}>
            {showJournal && journals.length > 0 && (
              <IconButton
                fullWidth
                variant={'filled'}
                color="var(--mantine-color-blue-7)"
                radius="md"
                h={70}
                icon={<IconNotebook size={16} />}
                labelStyles={{ fontWeight: 400 }}
                onClick={onAddJournalEntry}
              >
                {t('journal.addJournalEntry')}
              </IconButton>
            )}
            <Grid justify="center" align="center" gutter="lg">
              {showMedicalOverview && (
                <Grid.Col span={{ base: 12, sm: topSpan }}>
                  <IconButton
                    fullWidth
                    variant="default"
                    radius="md"
                    h={124}
                    size="xl"
                    p={'xs'}
                    gap="16px"
                    labelStyles={{ color: 'var(--mantine-color-blue-7)' }}
                    icon={<IconChartArcs size={32} />}
                    onClick={() =>
                      onClickButton('/patientDashboard/patientMedicalOverview')
                    }
                  >
                    {t('navbar.medicalOverview')}
                  </IconButton>
                </Grid.Col>
              )}
              {showMedicalInformation && (
                <Grid.Col span={{ base: 12, sm: topSpan }}>
                  <IconButton
                    fullWidth
                    variant="default"
                    radius="md"
                    h={124}
                    size="xl"
                    p={'xs'}
                    gap="16px"
                    labelStyles={{ color: 'var(--mantine-color-blue-7)' }}
                    icon={<IconClipboardPlus size={32} />}
                    onClick={() =>
                      onClickButton(
                        '/patientDashboard/patientMedicalInformation',
                      )
                    }
                  >
                    {t('navbar.medicalInformation')}
                  </IconButton>
                </Grid.Col>
              )}
              {showMyConsent && (
                <Grid.Col span={{ base: 12, sm: topSpan }}>
                  <IconButton
                    fullWidth
                    variant="white"
                    radius="md"
                    h={124}
                    size="xl"
                    p={'xs'}
                    gap="16px"
                    labelStyles={{ color: 'var(--mantine-color-blue-7)' }}
                    icon={<IconFileCheck size={32} />}
                    onClick={() =>
                      onClickButton('/patientDashboard/eicfConsentView')
                    }
                  >
                    {t('navbar.myConsent')}
                  </IconButton>
                </Grid.Col>
              )}
              {showMedicalInformation && (
                <Grid.Col span={bottomSpan}>
                  <IconButton
                    fullWidth
                    variant={'filled'}
                    color="var(--mantine-color-blue-7)"
                    radius="md"
                    h={70}
                    icon={<IconFlag size={16} />}
                    labelStyles={{ fontWeight: 400 }}
                    onClick={() =>
                      onClickButton('/patientDashboard/lifeEvents/0')
                    }
                  >
                    {t('table.lifeEvents.addLifeEvent')}
                  </IconButton>
                </Grid.Col>
              )}
              {showPhysiologicals && (
                <Grid.Col span={bottomSpan}>
                  <IconButton
                    fullWidth
                    variant={'filled'}
                    color="var(--mantine-color-blue-7)"
                    radius="md"
                    h={70}
                    icon={<IconHeartbeat size={16} />}
                    labelStyles={{ fontWeight: 400 }}
                    onClick={() =>
                      onClickButton('/patientDashboard/physiologicalData/0')
                    }
                  >
                    {t('physiologicalData.addPhysiologicalData')}
                  </IconButton>
                </Grid.Col>
              )}
            </Grid>
          </Stack>
          {isEicfLocked && (
            <Overlay
              color="#fff"
              backgroundOpacity={0.55}
              blur={2}
              radius="md"
              zIndex={5}
            />
          )}
        </Box>
      </Stack>
    );
  }),
);

export default PatientOverview;

interface IconButtonProps extends ButtonProps {
  icon?: ReactNode;
  labelStyles?: CSSProperties;
  gap?: string | number;
  onClick?: () => void;
}

const IconButton: React.FC<IconButtonProps> = ({
  icon,
  labelStyles,
  children,
  gap = '5px',
  ...props
}) => {
  return (
    <Button
      {...props}
      styles={{
        label: {
          display: 'flex',
          flexDirection: 'column',
          gap,
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          whiteSpace: 'pre-wrap',
          ...labelStyles,
        },
      }}
    >
      {icon}
      {children}
    </Button>
  );
};
